import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShieldCheck, Upload, FileText, Download, Trash2, CheckCircle2, AlertCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ensureDLToken } from "@/lib/dlToken";
import { fmtDate } from "@/lib/datetime";
import { HipaaTemplateCard } from "@/components/HipaaTemplateCard";
import { hipaaStatus } from "@/components/HipaaSendButton";

const BANNER_URL = "https://customer-assets-wrfwihn1.emergentagent.net/job_pharmacy-dispatch-3/artifacts/q79581g3_HIPPA.jpg";

// Static compliance text — rendered on the right side of the page. Kept in
// a plain array so we can style headings/paragraphs cleanly without dropping
// a wall of markdown into JSX.
const COMPLIANCE_SECTIONS = [
  { h1: "HIPAA Compliance Requirements for Medical Couriers", p: "To maintain compliance, medical delivery services must implement the following safeguards:" },
  { h2: "1. Administrative Safeguards" },
  { list: [
    ["HIPAA Training", "All couriers must complete HIPAA compliance training to understand PHI handling, breach reporting, and security protocols."],
    ["Signed Business Associate Agreements (BAAs)", "If a courier service works with healthcare providers, a BAA is required to outline PHI protection responsibilities."],
    ["Risk Assessments", "Regular audits should identify vulnerabilities in data handling and transportation."],
  ]},
  { h2: "2. Physical Safeguards" },
  { list: [
    ["Secure Storage", "PHI must be stored in locked compartments during transit."],
    ["Tamper-Proof Packaging", "Use sealed, opaque bags or containers to prevent unauthorized access."],
    ["Chain of Custody Logs", "Document every handoff to ensure accountability."],
  ]},
  { h2: "3. Technical Safeguards" },
  { list: [
    ["Encrypted Tracking Systems", "Delivery tracking software should encrypt PHI to prevent hacking."],
    ["Secure Communication", "Disposable waybills or digital systems should avoid exposing patient details."],
    ["Access Controls", "Only authorized personnel should handle PHI."],
  ]},
  { h1: "Best Practices for HIPAA-Compliant Medical Courier Services" },
  { h2: "1. Partner with HIPAA-Compliant Couriers" },
  { p: "Healthcare providers should only work with medical couriers that sign BAAs, provide proof of HIPAA training, and use secure vehicles with GPS tracking." },
  { h2: "2. Minimize PHI Exposure" },
  { list: [
    ["Redact unnecessary patient details", "from shipping labels."],
    ["Use coded identifiers", "instead of full patient names."],
  ]},
  { h2: "3. Train Employees Regularly" },
  { list: [
    ["Annual HIPAA training refreshers", "keep couriers updated on regulations."],
    ["Simulated breach drills", "help teams respond effectively to data leaks."],
  ]},
  { h2: "4. Implement Secure Delivery Protocols" },
  { list: [
    ["Require recipient signatures", "for PHI deliveries."],
    ["Verify identities", "before releasing sensitive packages."],
  ]},
  { h2: "5. Use Technology for Compliance" },
  { list: [
    ["Electronic Proof of Delivery (ePOD)", "systems with audit trails."],
    ["Temperature-controlled transport", "for sensitive medical specimens."],
  ]},
  { h1: "What Happens If a HIPAA Breach Occurs?" },
  { p: "Despite precautions, breaches can happen — lost packages, unauthorized access, or cyberattacks. If PHI is compromised:" },
  { h2: "Report the Breach" },
  { list: [
    ["Notify the healthcare provider and affected patients", "within 60 days."],
    ["File a report with the U.S. Department of Health and Human Services (HHS)", "if 500+ patients are affected."],
  ]},
  { h2: "Investigate & Remediate" },
  { list: [
    ["Identify how the breach occurred."],
    ["Strengthen security measures", "to prevent future incidents."],
  ]},
  { h2: "Legal & Financial Consequences" },
  { list: [
    ["Fines range from $100 to $50,000", "per violation."],
    ["Repeated violations", "can lead to criminal charges."],
  ]},
  { h1: "Choosing a HIPAA-Compliant Medical Courier Service" },
  { p: "Not all couriers meet healthcare compliance standards. When selecting a provider, ask:" },
  { list: [
    ["Do they sign BAAs?", "(Mandatory for HIPAA compliance)"],
    ["What security measures do they use?", "(Encryption, tamper-proof bags, GPS tracking)"],
    ["Is staff trained in HIPAA protocols?", "(Proof of training should be available)"],
  ]},
  { p: "Reputable medical logistics companies will have documented policies for PHI protection." },
  { h1: "Final Thoughts: Staying Compliant in Medical Courier Services" },
  { p: "HIPAA compliance isn't optional — it's a legal and ethical obligation for medical couriers. By implementing strict patient data security measures, conducting regular HIPAA training, and following courier HIPAA guidelines, delivery services can safeguard PHI and maintain trust with healthcare partners." },
  { p: "For healthcare providers, verifying a courier's compliance before outsourcing deliveries is crucial. A single breach can lead to massive penalties, making HIPAA-compliant medical logistics a non-negotiable requirement." },
  { p: "By staying informed and proactive, medical couriers can ensure safe, secure, and legally compliant healthcare deliveries." },
];

function ComplianceContent() {
  return (
    <article className="prose prose-sm max-w-none space-y-4 text-foreground" data-testid="hipaa-compliance-text">
      {COMPLIANCE_SECTIONS.map((s, idx) => {
        if (s.h1) {
          return (
            <React.Fragment key={idx}>
              <h2 className="text-xl font-bold text-primary mt-6 mb-2 tracking-tight border-b border-border/60 pb-1.5">{s.h1}</h2>
              {s.p && <p className="text-sm text-muted-foreground leading-relaxed">{s.p}</p>}
            </React.Fragment>
          );
        }
        if (s.h2) {
          return <h3 key={idx} className="text-sm font-semibold text-foreground mt-4 mb-1">{s.h2}</h3>;
        }
        if (s.p) {
          return <p key={idx} className="text-sm text-muted-foreground leading-relaxed">{s.p}</p>;
        }
        if (s.list) {
          return (
            <ul key={idx} className="text-sm space-y-1.5 pl-4 list-disc marker:text-primary/50">
              {s.list.map(([label, body], j) => (
                <li key={j}>
                  <span className="font-semibold">{label}</span>
                  {body ? <>: <span className="text-muted-foreground">{body}</span></> : null}
                </li>
              ))}
            </ul>
          );
        }
        return null;
      })}
    </article>
  );
}

function fmtBytes(n) {
  if (n == null) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

// Left column: driver list. Super Admin can upload/replace/delete; Pharmacy
// Staff and Dispatcher see a read-only view with a "Download" button when a
// document exists.
function DriverRoster({ rows, canEdit, onChanged, onSelect, selectedId }) {
  const fileRefs = useRef({});
  const [busy, setBusy] = useState(null);

  const pickFile = (driverId) => {
    fileRefs.current[driverId]?.click();
  };

  const doUpload = async (driverId, file) => {
    if (!file) return;
    setBusy(driverId);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      await api.post(`/hipaa/drivers/${driverId}/document`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Document uploaded for ${rows.find((r) => r.driver_id === driverId)?.driver_name || "driver"}`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(null);
    }
  };

  const doDelete = async (driverId) => {
    if (!window.confirm("Remove the HIPAA document for this driver? This cannot be undone.")) return;
    setBusy(driverId);
    try {
      await api.delete(`/hipaa/drivers/${driverId}/document`);
      toast.success("Document removed");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(null);
    }
  };

  const doDownload = async (driverId, doc) => {
    // ensureDLToken() will refresh the short-lived (5 min) download token
    // when it's missing or near expiry — getDLToken() alone returns the
    // stale cached value and causes 401 "Token expired" on the file URL.
    try {
      const token = await ensureDLToken();
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/hipaa/drivers/${driverId}/document?auth=${encodeURIComponent(token)}`;
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open document — please try again.");
    }
  };

  return (
    <div className="space-y-2" data-testid="hipaa-driver-roster">
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No drivers on file.</p>
      ) : (
        rows.map((r) => {
          const hasDoc = !!r.doc;
          const isSelected = selectedId === r.driver_id;
          return (
            <Card
              key={r.driver_id}
              className={`p-3 space-y-2 cursor-pointer transition-colors ${isSelected ? "border-primary/60 bg-primary/[0.03]" : "hover:bg-accent/10"}`}
              onClick={() => onSelect(r.driver_id)}
              data-testid={`hipaa-driver-card-${r.driver_id}`}
            >
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{r.driver_name || r.driver_email}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{r.driver_email}</div>
                </div>
                {hasDoc ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" title="Document on file" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-amber-500 shrink-0" title="No document" />
                )}
              </div>

              {hasDoc ? (
                <div className="rounded border border-border/60 bg-secondary/40 p-2 text-[11px] space-y-0.5">
                  <div className="flex items-center gap-1.5 font-medium truncate" data-testid={`hipaa-doc-name-${r.driver_id}`}>
                    <FileText className="h-3.5 w-3.5 text-primary shrink-0" />
                    <span className="truncate">{r.doc.filename}</span>
                  </div>
                  <div className="text-muted-foreground">
                    {fmtBytes(r.doc.size)} · uploaded {fmtDate(r.doc.uploaded_at)}
                  </div>
                </div>
              ) : (
                <div className="text-[11px] text-muted-foreground italic">No compliance document uploaded yet.</div>
              )}
              {r.request && (
                <div
                  className={`text-[10px] font-medium ${hipaaStatus(r) === "signed" ? "text-emerald-700" : "text-amber-700"}`}
                  data-testid={`hipaa-request-status-${r.driver_id}`}
                >
                  {r.request.status === "signed"
                    ? `Signed by driver (${r.request.method === "esign" ? "online e-sign" : "uploaded copy"}) · ${fmtDate(r.request.signed_at)}`
                    : `Sent for signature ${fmtDate(r.request.sent_at)} — waiting`}
                </div>
              )}

              <div className="flex flex-wrap gap-1.5" onClick={(e) => e.stopPropagation()}>
                {hasDoc && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    onClick={() => doDownload(r.driver_id, r.doc)}
                    data-testid={`hipaa-download-${r.driver_id}`}
                  >
                    <Download className="h-3 w-3 mr-1" /> View
                  </Button>
                )}
                {canEdit && (
                  <>
                    <input
                      ref={(el) => { fileRefs.current[r.driver_id] = el; }}
                      type="file"
                      accept="application/pdf,image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={(e) => doUpload(r.driver_id, e.target.files?.[0])}
                      data-testid={`hipaa-upload-input-${r.driver_id}`}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      disabled={busy === r.driver_id}
                      onClick={() => pickFile(r.driver_id)}
                      data-testid={`hipaa-upload-btn-${r.driver_id}`}
                    >
                      <Upload className="h-3 w-3 mr-1" />
                      {hasDoc ? "Replace" : "Upload"}
                    </Button>
                    {hasDoc && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs text-destructive hover:text-destructive"
                        disabled={busy === r.driver_id}
                        onClick={() => doDelete(r.driver_id)}
                        data-testid={`hipaa-delete-${r.driver_id}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </>
                )}
              </div>
            </Card>
          );
        })
      )}
    </div>
  );
}

// Right column: main compliance text. When a driver is selected AND has a
// doc, the top of the column shows an inline preview iframe (PDF / image)
// so pharmacy staff can review without navigating away.
function DocumentPreview({ driver, onClose }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  useEffect(() => {
    if (!driver?.doc) { setPreviewUrl(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const token = await ensureDLToken();
        if (cancelled) return;
        setPreviewUrl(`${process.env.REACT_APP_BACKEND_URL}/api/hipaa/drivers/${driver.driver_id}/document?auth=${encodeURIComponent(token)}`);
      } catch (e) {
        if (!cancelled) setPreviewUrl(null);
      }
    })();
    return () => { cancelled = true; };
  }, [driver?.driver_id, driver?.doc?.file_id]);

  if (!driver?.doc || !previewUrl) return null;
  const isPdf = driver.doc.content_type === "application/pdf";
  return (
    <Card className="p-4 mb-6" data-testid="hipaa-doc-preview">
      <div className="flex items-center gap-2 mb-3">
        <FileText className="h-4 w-4 text-primary" />
        <div className="text-sm font-semibold flex-1 truncate">
          {driver.driver_name} — {driver.doc.filename}
        </div>
        <Button size="sm" variant="outline" onClick={() => window.open(previewUrl, "_blank")}>
          <Download className="h-3.5 w-3.5 mr-1" /> Open
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose}>Close preview</Button>
      </div>
      {isPdf ? (
        <iframe
          src={previewUrl}
          title="HIPAA document preview"
          className="w-full h-[520px] rounded border border-border/60 bg-secondary/40"
          data-testid="hipaa-doc-iframe"
        />
      ) : (
        <img
          src={previewUrl}
          alt="HIPAA document preview"
          className="w-full max-h-[520px] object-contain rounded border border-border/60 bg-secondary/40"
          data-testid="hipaa-doc-img"
        />
      )}
    </Card>
  );
}

export default function HIPAA() {
  const { user } = useAuth();
  const canEdit = user?.role === "super_admin";
  const [rows, setRows] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/hipaa/drivers");
      setRows(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const selected = useMemo(() => rows.find((r) => r.driver_id === selectedId) || null, [rows, selectedId]);

  return (
    <div className="p-6 lg:p-10 max-w-[1400px] mx-auto" data-testid="hipaa-page">
      <header className="mb-6">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-[11px] uppercase tracking-[0.18em] font-semibold mb-3">
          <ShieldCheck className="h-3 w-3" /> HIPAA
        </div>
        <h1 className="text-3xl lg:text-4xl font-display font-bold tracking-tight">HIPAA Compliance</h1>
        <p className="text-muted-foreground mt-1.5 max-w-2xl">
          {canEdit
            ? "Upload and manage compliance documents for each driver. Pharmacy Staff can view and download these documents to verify a driver's compliance before releasing PHI."
            : "Compliance documents for each driver on the platform. Tap a driver to preview or download their HIPAA certificate."}
        </p>
      </header>

      <img
        src={BANNER_URL}
        alt="HIPAA Compliance for Medical Couriers — Santa Rosa Commercial Investment"
        className="w-full max-w-[750px] rounded-xl border border-border/60 shadow-sm mb-8 mx-auto"
        data-testid="hipaa-banner"
      />

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
        <aside className="space-y-3" data-testid="hipaa-left-column">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Drivers</h2>
            <span className="text-[11px] text-muted-foreground">{rows.length} total</span>
          </div>
          {canEdit && <HipaaTemplateCard />}
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (
            <DriverRoster
              rows={rows}
              canEdit={canEdit}
              onChanged={load}
              onSelect={setSelectedId}
              selectedId={selectedId}
            />
          )}
        </aside>

        <section data-testid="hipaa-right-column">
          {selected?.doc && (
            <DocumentPreview driver={selected} onClose={() => setSelectedId(null)} />
          )}
          <ComplianceContent />
        </section>
      </div>
    </div>
  );
}
